import { service } from '@ember/service';

import type { DSData, DSSetup, PopupData, Service } from 'webapp-types';
import {
	CompareType,
	DataSelectType,
	FakeModelIdPrefix,
	ModelName,
	PopupEvent,
} from 'webapp/enums/enums';
import { uniqueId } from 'webapp/utils/common';

import CommonPopupMetricSelectionComponent from 'webapp/components/common/popups/metric-selection';
import DataSelectEditPopupComponent from 'webapp/components/data-select/edit/popup';

import DataSelect from 'webapp/models-custom/data-select';
import StatusObject from 'webapp/models-custom/status-object';
import type DataSelectRecord from 'webapp/models/data-select';
import type DChartPrimaryPoint from 'webapp/d-chart/point/primary-point';

import ModelManagerForStoreService from 'webapp/services/model-manager/for-store';
import type { RSPromise } from 'webapp/services/request';

export default class ModelManagerDataSelectsService extends ModelManagerForStoreService<
	DataSelectRecord,
	DataSelect
> {
	@service('request') declare requestService: Service.Request;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('popup') declare popupService: Service.Popup;
	@service('model-manager/samples') declare mmSamplesService: Service.ModelManager.Samples;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;

	getRecordsForBlock(blockId: number): DataSelectRecord[] {
		// also unsaved!
		return this.allRecords
			.filter((item) => item.blockId === blockId)
			.filter((item) => !item.isDeleted);
	}

	generateFakeUUID() {
		return -1 * Number(`${FakeModelIdPrefix.OTHER}${uniqueId()}`);
	}

	fetchData(dataSelect: DataSelect, skipStatusObjectInfolayer = false) {
		const rejectImmediately = () => {
			const promise: RSPromise<never> = new Promise((_resolve, reject) => {
				reject();
			});

			promise.jqPromise = undefined;

			return promise;
		};

		if (!dataSelect.metricSetup.key) {
			return rejectImmediately();
		}

		const generateFakeId = () => {
			// these are not used for anything, only BE needs them
			return this.generateFakeUUID();
		};

		const data = dataSelect.toSerializedJSON();
		if (!data.id) {
			data.id = generateFakeId();
		}
		(data.compares || []).forEach((compare) => {
			if (!compare.id) {
				compare.id = generateFakeId();
			}
		});

		const queryPromise = this.requestService.api<DSData.RootTop>(
			'POST',
			'/dataquery',
			{ dataSelect: data },
			{ timeout: 60 * 1000 },
		);
		/** /
		const queryPromise = new Promise((res) => {
			// eslint-disable-next-line quotes, comma-spacing, key-spacing, block-spacing
			const reply = window.dbx_mock['POST /d/dataquery'];
			setTimeout(() => {
				res(reply);
			}, 1000);
		});
		queryPromise.jqPromise = { abort: () => console.log('aborting') };
		/**/

		const promise: RSPromise<DSData.RootTop> = queryPromise.then((reply) => {
			if (!reply) {
				return rejectImmediately();
			}
			const statusObjects = StatusObject.createFromDataArray(reply.statusObjects);
			if (dataSelect.blockId) {
				statusObjects.forEach((so: StatusObject) => {
					so.blockId = dataSelect.blockId;
				});
			}

			if (statusObjects.length && !skipStatusObjectInfolayer) {
				this.infolayerService.statusObjects(statusObjects);
			}

			return reply;
		});

		// make also this promise cancelable
		promise.jqPromise = queryPromise.jqPromise;

		return promise;
	}

	getPointsFromData(dataSelect: DataSelect, data: DSData.RootTop | null) {
		if (!data) {
			return [];
		}

		const dataRoot = dataSelect.createDataRoot(data),
			points: DChartPrimaryPoint[] = [];

		(dataRoot?.outputs || []).forEach((output) => {
			const p = this.mmSamplesService.createDChartPointsFromOutput(output);
			points.push(...p);
		});

		return points;
	}

	openMetricSelectWithAttributes(
		options: Omit<PopupData.Common.MetricSelection, 'onSave'> = {},
	): Promise<DSSetup.MetricSetupData> {
		return new Promise((resolve, reject) => {
			let waitingForAttributePopup = false;

			const checkToOpenMetricSelection = (
				selectedMetricSetupData: DSSetup.MetricSetupData,
			) => {
				const needsAdditionalConfig =
					!options.skipAdditionalProperties &&
					this.checkIfMetricNeedsAdditionalConfig(selectedMetricSetupData);

				if (needsAdditionalConfig) {
					waitingForAttributePopup = true;

					// open the attribute selection popup with pre-selected metric
					const attributePopup = this.popupService.createModal(
						CommonPopupMetricSelectionComponent,
						{
							allowStacked: true,
							popupClass: 'popup-type-add-my-metric',
						},
					);

					options.metricSetupData = selectedMetricSetupData;
					const popupData = Object.assign(
						{
							onSave: (finalMetricSetupData: DSSetup.MetricSetupData) => {
								resolve(finalMetricSetupData);
							},
						},
						options,
					);

					attributePopup.open(popupData);
					attributePopup.on(PopupEvent.CLOSED, this, () => {
						reject();
					});
				} else {
					resolve(selectedMetricSetupData);
				}
			};

			this.mmMetricsService
				.openMetricSelectPopup(
					Object.assign({}, options.metricSelectOptions || {}, {
						onChange: checkToOpenMetricSelection,
					}),
				)
				.then((metricSetupData: DSSetup.MetricSetupData) => {
					resolve(metricSetupData);
				})
				.catch(() => {
					// only reject if we're not waiting for the attribute popup
					if (!waitingForAttributePopup) {
						reject();
					}
				});
		});
	}

	openAdvancedPopup(data: PopupData.DataSelect.Edit) {
		const popup = this.popupService.createModal(DataSelectEditPopupComponent, {
			popupClass: 'popup-dataselect-edit',
			allowStacked: true,
			allowMultipleInstances: true,
		});

		popup.open(data);

		return popup;
	}

	getCompareTypeFromDataSelectType(dataSelectType: DataSelectType) {
		switch (dataSelectType) {
			case DataSelectType.GOAL:
				return CompareType.GOAL;
			case DataSelectType.FORECAST:
				return CompareType.FORECAST;
			case DataSelectType.FORECAST_OPTIMISTIC:
				return CompareType.FORECAST_OPTIMISTIC;
			case DataSelectType.FORECAST_PESSIMISTIC:
				return CompareType.FORECAST_PESSIMISTIC;
			default:
				return CompareType.RANGE;
		}
	}

	getTypeText(dataSelectType: DataSelectType) {
		switch (dataSelectType) {
			case DataSelectType.MAIN:
				return 'Main';
			case DataSelectType.COMPARE:
				return 'Compare';
			case DataSelectType.GOAL:
				return 'Goal';
			case DataSelectType.AVERAGE:
				return 'Average';
			case DataSelectType.TREND:
				return 'Trend';
			case DataSelectType.FORECAST:
				return 'Forecast';
			case DataSelectType.FORECAST_OPTIMISTIC:
				return 'Forecast (optimistic)';
			case DataSelectType.FORECAST_PESSIMISTIC:
				return 'Forecast (pessimistic)';
			case DataSelectType.MEDIAN:
				return 'Median';
			case DataSelectType.ANOMALY:
				return 'Anomaly';
			default:
				return 'Unknown';
		}
	}

	checkIfMetricNeedsAdditionalConfig(metricSetupData: DSSetup.MetricSetupData): boolean {
		const metric = this.mmMetricsService.getMetricForKeyAndSpaceAccessId(
			metricSetupData.key,
			metricSetupData.spaceAccessId,
		);

		if (!metric) {
			return false;
		}

		return metric.allowDimensions || metric.hasUnits;
	}

	// ------ For-store ------

	modelName = ModelName.DATA_SELECT;

	createModel(model: DataSelectRecord): DataSelect {
		return new DataSelect({ model });
	}
}

declare module '@ember/service' {
	interface Registry {
		'model-manager/data-selects': ModelManagerDataSelectsService;
	}
}
